import { PLATFORMS } from '@domain/platform/platform.constants';
import { DEFAULT_METRICS } from '@shared/constants/MetricsTypeConstants';
import { convertAllCapsSnakeCaseToTitleCase, limitNumberOfCharacters } from '@shared/utils/strings';
import {
    MANUAL_STRATEGY_TYPE,
    MAX_CHARACTERS_FOR_CAMPAIGNS_NAME,
    TARGET_IMPRESSION_SHARE,
    TARGET_STRATEGY_TYPE,
} from '@domain/segments/segments-modal';
import { GetPathnameFrom } from '@domain/campaigns/campaigns.types';
import { match } from '@shared/utils/match';
import Routes from '@domain/router/routes.constants';
import { CAMPAIGN_STATUS } from './campaigns.constants';
import { ConvertCampaignsToHashArgs } from './campaigns.interfaces';

const isEnabled = campaign => campaign.status === CAMPAIGN_STATUS.ENABLED;

const isFacebookAds = campaign => campaign.platform === PLATFORMS.FACEBOOK_ADS.value;

export const isActive = campaign => isEnabled(campaign) || hasMetrics(campaign);

const hasMetrics = campaign => Object.keys(DEFAULT_METRICS).reduce(
    (previous, metric) => (
        previous || (
            metric in campaign
            && Boolean(campaign[metric])
            && campaign[metric] !== 0)),
    false,
);

export const formatFieldValue = (field, fieldValue) => (field === 'status'
    ? toggleStatusValue(fieldValue)
    : fieldValue);

export const toggleStatusValue = status => [CAMPAIGN_STATUS.ENABLED, CAMPAIGN_STATUS.PAUSED]
    .find(value => value !== status);

export const convertCampaignsToHash = ({
    report,
    extraFieldsGetter = getExtraFieldsForCampaign,
    key = 'campaign_id',
}: ConvertCampaignsToHashArgs) => {
    const { items: campaigns } = report;
    const campaignsBySpecifiedKey = {};

    for (let i = 0; i < campaigns.length; i += 1) {
        const campaign = campaigns[i];
        const extraFields = extraFieldsGetter(campaign);

        if (isKeySpecifiedOnCampaign(key, campaign)) {
            const keyValue = campaign[key];
            campaignsBySpecifiedKey[keyValue] = {
                ...campaign,
                ...extraFields,
            };
        }
    }

    return campaignsBySpecifiedKey;
};

const isKeySpecifiedOnCampaign = (key, campaign) => !!campaign?.[key];

const getExtraFieldsForCampaign = campaign => ({
    bid_strategy_type: getObjectiveForBidStrategy(campaign),
    is_cbo: !!campaign.bid_strategy_type,
});

export const formatBidStrategyForCampaigns = campaigns => campaigns.map(campaign => ({
    ...campaign,
    bid_strategy_type: getObjectiveForBidStrategy(campaign),
    is_cbo: !!campaign.bid_strategy_type,
}));

const getObjectiveForBidStrategy = (campaign) => {
    const isFacebookAdsCampaign = isFacebookAds(campaign);
    const isObjective = (campaign.objective && isFacebookAdsCampaign);

    const isBidStrategyType = (campaign.bid_strategy_type && !isFacebookAdsCampaign);

    const isTargetImpressionShare = campaign.bid_strategy_type === '--';

    if (isTargetImpressionShare) return TARGET_IMPRESSION_SHARE;

    return (
        (isObjective || isBidStrategyType)
        && limitNumberOfCharacters(
            getStrategyForCampaign(campaign, isFacebookAdsCampaign),
            MAX_CHARACTERS_FOR_CAMPAIGNS_NAME,
        )
    );
};

const formatStrategyTypeForGoogle = ({
    bid_strategy_type: bidStrategyType,
}) => MANUAL_STRATEGY_TYPE[bidStrategyType]
    || TARGET_STRATEGY_TYPE[bidStrategyType]
    || convertAllCapsSnakeCaseToTitleCase(bidStrategyType);

// #1525 / github.com/paretogroup/platform-spa/issues/1525
// @TODO: Discutir a escalabalidade desse tratamento para casos futuros
// @psdaniel, @gustavosilvaf, @PedroDFigueiredo
const getStrategyForCampaign = (campaign, isFacebook) => (
    isFacebook
        ? convertAllCapsSnakeCaseToTitleCase(campaign.objective)
        : formatStrategyTypeForGoogle(campaign)
);

export const onCampaignManagerRoute = (
    getPathnameFrom: GetPathnameFrom,
    callback: Function,
) => {
    const selectors = {
        state: state => state.router.location.pathname,
        action: (_, action) => action.payload.location.pathname,
    };

    return match(
        selectors[getPathnameFrom],
        { [Routes.CAMPAIGN_MANAGER]: callback },
    );
};
